import React from 'react';
import { Users, GraduationCap, Heart, School } from 'lucide-react';
import { Avatar } from '../../shared/Avatar';
import { MasteryBar } from '../../shared/MasteryBar';
import { Badge } from '../../../../components/ui/Badge';
import { formatRelativeTime } from '../../../../utils/formatters';
import { formatGroupLabel } from '../../../../config/systemConfig';
import { ROLE_CONFIG } from './types';
import type { UserRow, RoleFilter } from './types';
export const learnerColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-7761" data-mp="rswqwtypcgqr">
        <Avatar name={v} data-id="element-7762" data-mp="cyiitogoerqv" />
        <div data-id="element-7763" data-mp="cza0rwhcoqyo">
          <div className="text-sm font-medium text-gray-900" data-id="element-7764" data-mp="czjw7zl5zqbr">{v}</div>
          {/* Was `row.grade` — een veld dat op UserRow niet bestaat, dus deze
              regel toonde bij ELKE leerling een streepje terwijl de groep één
              functie verderop al berekend werd. Geen typefout die de compiler
              meldt: het veld is optioneel, dus `undefined` is geldig. */}
          <div className="text-xs text-gray-400" data-id="element-7765" data-mp="bqt0lwe1mi9a">{row.groupName ?? '—'}</div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-7766" data-mp="lip2lij92sis">{v}</span>
}, {
  key: 'sessionCount',
  label: 'Sessies',
  render: (v: number) => <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-7767" data-mp="gkf4az28bbsy">
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Beheersing',
  render: (v: number) => <MasteryBar value={v || 0} data-id="element-7768" data-mp="3k02r7s6qcqpa" />
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7769" data-mp="1ug9955z0ghp">
        {formatRelativeTime(v, true)}
      </span>
}];
export const teacherColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-7770" data-mp="1d8lfy5xkj4gq">
        <Avatar name={v} data-id="element-7771" data-mp="1dbfy1qt40d6z" />
        <div data-id="element-7772" data-mp="1981i10567wtj">
          <div className="text-sm font-medium text-gray-900" data-id="element-7773" data-mp="198b103rst3pq">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-7774" data-mp="19a497ni27vzu">
            {row.groupName ?? (row.account && 'grade' in row.account ? formatGroupLabel(row.account.grade as number) : undefined) ?? '—'}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-7775" data-mp="ft78i5qarrk7">{v}</span>
}, {
  key: 'linkedCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7776" data-mp="3jca8xx8tq7fu">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-7777" data-mp="3oali05raodx0" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7778" data-mp="3savkh8fvom74">
        {formatRelativeTime(v, true)}
      </span>
}];
export const parentColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-7779" data-mp="30khh465hqs9p">
        <Avatar name={v} data-id="element-7780" data-mp="34mujqloxya29" />
        <span className="text-sm font-medium text-gray-900" data-id="element-7781" data-mp="34m2m75bsa29c">{v}</span>
      </div>
}, {
  key: 'linkedCount',
  label: 'Kinderen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7782" data-mp="2x18ucku8mb2s">
        <Heart className="w-3.5 h-3.5 text-rose-400" data-id="element-7783" data-mp="2s51laj6jojgm" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7784" data-mp="2j4d81qoz6n4s">
        {formatRelativeTime(v, true)}
      </span>
}];
export const allColumns = [{
  key: 'name',
  label: 'Gebruiker',
  render: (_value: unknown, row: UserRow) => <div className="flex items-center gap-3" data-id="element-7785" data-mp="29dio4sz7bzmi">
        <Avatar name={row.name} data-id="element-7786" data-mp="29bo2fcg3w3hd" />
        <div data-id="element-7787" data-mp="24e61a88bysjx">
          <div className="font-medium text-gray-900 text-sm" data-id="element-7788" data-mp="24fpgrwm5wzkn">{row.name}</div>
          <div className="text-xs text-gray-400" data-id="element-7789" data-mp="20dc8p1jy0pkp">{row.email}</div>
        </div>
      </div>
}, {
  key: 'role',
  label: 'Rol',
  render: (_value: unknown, row: UserRow) => <Badge variant={ROLE_CONFIG[row.role].variant} size="sm" data-id="element-7790" data-mp="1vg2c91y4zdnd">
        {ROLE_CONFIG[row.role].label}
      </Badge>
}, {
  key: 'organization',
  label: 'Organisatie',
  render: (v: string) => <span className="text-sm text-gray-600 truncate max-w-[140px] block" data-id="element-7791" data-mp="3qhxqa5wqvwon">
        {v}
      </span>
}, {
  key: 'linkedCount',
  label: 'Koppelingen',
  render: (v: number) => <span className="text-sm text-gray-700" data-id="element-7792" data-mp="3gqb8trttd1dg">{v > 0 ? v : '—'}</span>
}, {
  key: 'xp',
  label: 'XP',
  render: (v: number, row: UserRow) => row.role === 'learner' ? <span className="text-sm font-bold text-violet-600" data-id="element-7793" data-mp="37qo52zdm3jh8">
          {v.toLocaleString('nl-NL')}
        </span> : <span className="text-xs text-gray-400" data-id="element-7794" data-mp="3brzzdi7fksvr">—</span>
}, {
  key: 'lastActiveFormatted',
  label: 'Actief',
  render: (v: string) => <span className="text-xs text-gray-500" data-id="element-7795" data-mp="2xtif5ugqhyil">{v}</span>
}];
export const schoolColumns = [{
  key: 'name',
  label: 'School',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-7796" data-mp="32to0ksweuos8">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-7797" data-mp="w25b17mlbunl">
          <School className="w-4 h-4 text-purple-600" data-id="element-7798" data-mp="10z5dl8k8ef9y" />
        </div>
        <span className="text-sm font-medium text-gray-900" data-id="element-7799" data-mp="2lhzful744zr9">{v}</span>
      </div>
}, {
  key: 'learnerCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7800" data-mp="2gkzbqfttq72s">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-7801" data-mp="2v9lqafbmmusy" />
        {v}
      </span>
}, {
  key: 'teacherCount',
  label: 'Docenten',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7802" data-mp="348gwhreo84w9">
        <GraduationCap className="w-3.5 h-3.5 text-gray-400" data-id="element-7803" data-mp="2z9nuow8qy2bx" />
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Gem. beheersing',
  render: (v: number) => <MasteryBar value={v} data-id="element-7804" data-mp="3e6ekqewj61ov" />
}];
export function getColumnsForRole(roleFilter: RoleFilter) {
  switch (roleFilter) {
    case 'learner':
      return learnerColumns;
    case 'teacher':
      return teacherColumns;
    case 'parent':
      return parentColumns;
    case 'school':
      return schoolColumns;
    default:
      return allColumns;
  }
}